import { ChangeDetectorRef, Component, ElementRef, Input, KeyValueDiffers, OnChanges, OnDestroy, OnInit } from '@angular/core';

import { AbstractPageBuilderService } from '../../services/abstract-page-builder.service';
import { BlockComponent } from '../Block/block.component';
import { ClickOutsideService } from '../../services/click-outside.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { BehaviorSubject, combineLatest, filter, tap } from 'rxjs';
import { DomHandler } from 'primeng/dom';
import { DndDropEvent } from 'ngx-drag-drop';
import { BlockTypeModel } from '../../models/block-type.model';
import { BlockModel } from '../../models/block.model';
import { CuratorBlock } from '../../models/curator-block.model';
import { CuratorDisplayElement } from '../../models/curator-display-element.model';
import { CuratorBlockType } from '../../models/curator-block-type.model';
import { isMentionBlockTypes } from '../../../../Monitor/Briefings/enums/briefing-block-type.enum';

@Component({
    selector: 'agpr-curator-block',
    templateUrl: './curator-block.component.html',
    styleUrls: ['./curator-block.component.scss'],
    standalone: false
})
export class CuratorBlockComponent extends BlockComponent implements OnInit, OnChanges, OnDestroy {
    readonly moveUpButtonClassName: string = 'agpr-layoutBlock-moveUpButton';
    readonly moveDownButtonClassName: string = 'agpr-layoutBlock-moveDownButton';
    readonly buttonTypes = ButtonTypesEnum;

    @Input() declare block: CuratorBlock;
    @Input() actionMenuTextAlignment: string;

    showEmptySectionWarning: boolean = false;

    scrollableParents;
    toolbarElement: HTMLElement;
    blockMaxWidth: number = 640;
    placeholderHeight: number = 83;
    openedPlaceholderIndex: number = -1;
    disableDrop: boolean = false;
    draggingBlock: BlockModel;
    draggingBlockType: BlockTypeModel;
    draggingBlockPosition: string;
    textBlockToolbarFixedTop: number = 0;
    private displayElement$: BehaviorSubject<CuratorDisplayElement> = new BehaviorSubject<CuratorDisplayElement>(null);
    private getBlockMaxWidth$ = this.pageBuilderService.getBlockMaxWidth().pipe(
        filter(x => !!x),
        tap(x => {
            this.blockMaxWidth = x;
        })
    );

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        protected changeDetectorRef: ChangeDetectorRef,
        public clickOutsideService: ClickOutsideService,
        public el: ElementRef
    ) {
        super(differs, pageBuilderService, changeDetectorRef, clickOutsideService, el);
    }

    private get containsMentionBlock(): boolean {
        return this.block.blocks.some(b => isMentionBlockTypes(b.type));
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.push(
            combineLatest([
                this.pageBuilderService.getBlocks(),
                this.pageBuilderService.shouldShowLayoutBlockEmptySectionWarning$()
            ]).subscribe(([_, warn]) => {
                this.showEmptySectionWarning = this.containsMentionBlock && warn;
                this.changeDetectorRef.detectChanges();
            }),
            combineLatest([
                this.getBlockMaxWidth$,
                this.displayElement$.pipe(filter(x => !!x))
            ]).subscribe(([blockMaxWidth, displayElement]) => {
                this.calculatePlaceholderHeight(blockMaxWidth, displayElement);
            }),
            combineLatest([
                this.getBlockMaxWidth$,
                this.pageBuilderService.getTextBlockToolbarFixedTop().pipe(
                    filter(x => !!x),
                    tap(y => {
                        this.textBlockToolbarFixedTop = y;
                    })
                )
            ]).subscribe(([blockMaxWidth, textBlockToolbarFixedTop]) => this.adjustToolbarPosition()),
            this.pageBuilderService.getBlockEditingState().subscribe(isEditing => {
                this.scrollableParents = DomHandler.getScrollableParents(this.el.nativeElement);
                if (this.scrollableParents) {
                    if (isEditing) {
                        this.scrollableParents.forEach(element => {
                            element.addEventListener('scroll', () => this.adjustToolbarPosition());
                        });
                    } else {
                        this.scrollableParents.forEach(element => {
                            element.removeEventListener('scroll', () => this.adjustToolbarPosition());
                        });
                        this.toolbarElement = null;
                    }
                }
                this.changeDetectorRef.detectChanges();
            }),
            this.pageBuilderService.getDraggingBlock().subscribe(block => {
                this.draggingBlock = block;
                this.changeDetectorRef.detectChanges();
            }),
            this.pageBuilderService.getDraggingBlockType().subscribe(blockType => {
                this.draggingBlockType = blockType;
                this.disableDrop = (this.block.type as CuratorBlockType).disableDropCondition?.(blockType) ?? false;
                this.changeDetectorRef.detectChanges();
            }),
            this.pageBuilderService.getFocusedBlockInfo().subscribe(focusedBlock => {
                const blockPosition = focusedBlock?.blockPosition?.split('.')[0];
                if (blockPosition !== this.blockPosition) {
                    return;
                }
                if (this.block.blocks.length === 0) {
                    const column = this.el.nativeElement.querySelectorAll('.agpr-curatorBlock-column')[0];
                    column.querySelector('.agpr-curatorBlock-placeholder-addButton .agpr-button')?.focus();
                }
                this.changeDetectorRef.detectChanges();
            })
        );
    }

    ngOnChanges(): void {
        super.ngOnChanges();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', () => this.adjustToolbarPosition());
            });
        }
    }

    inlineAddToggle(event: Event, element?: any, componentClass: string = 'agpr-block', placeholderIndex: number = -1): boolean {
        const isShowing = super.inlineAddToggle(event, element, componentClass);
        this.openedPlaceholderIndex = isShowing ? placeholderIndex : -1;
        return isShowing;
    }

    leaveContextMenu(container: HTMLElement, event?: any, mouseLeave?: boolean, componentClass: string = 'agpr-block'): void {
        super.leaveContextMenu(container, event, mouseLeave, componentClass);
        this.openedPlaceholderIndex = -1;
    }

    calculatePlaceholderHeight(blockMaxWidth: number, displayElement: CuratorDisplayElement): void {
        let contentWidth = blockMaxWidth;
        const padding = displayElement.styles.padding;
        if (padding) {
            const paddingWidth = padding.paddingOption ? padding.leftPadding + padding.rightPadding : padding.paddingAll * 2;
            contentWidth -= paddingWidth;
        }
        const columnSpacingWidth = displayElement.styles.columnSpacing;
        if (columnSpacingWidth) {
            contentWidth -= columnSpacingWidth * (displayElement.styles.width.length - 1);
        }
        const minWidth = Math.min(...displayElement.styles.width.map(width => {
            return contentWidth * parseInt(width, 10) / 100;
        }) as number[]);
        if (minWidth <= 144) {
            this.placeholderHeight = 111;
        } else if (minWidth <= 178) {
            this.placeholderHeight = 95;
        } else {
            this.placeholderHeight = 83;
        }
    }

    adjustToolbarPosition(): void {
        if (!this.toolbarElement) {
            this.toolbarElement = this.el.nativeElement.querySelector('.ck-toolbar');
        }
        if (!this.toolbarElement) {
            return;
        }

        this.toolbarElement.style.width = this.blockMaxWidth + 'px';
        const curatorBlockBoundingRect = this.el.nativeElement.querySelector('.agpr-curatorBlock-container').getBoundingClientRect();
        const toolbarBoundingRect = this.toolbarElement.getBoundingClientRect();
        const highestPosition = this.textBlockToolbarFixedTop + toolbarBoundingRect.height;
        if (curatorBlockBoundingRect.y <= highestPosition) {
            this.toolbarElement.style.position = 'fixed';
            this.toolbarElement.style.top = this.textBlockToolbarFixedTop + 'px';
            this.toolbarElement.style.zIndex = '3';
        } else {
            this.toolbarElement.style.position = 'static';
            this.toolbarElement.style.top = 'unset';
            this.toolbarElement.style.zIndex = 'auto';
        }
    }

    onDropBlocks(event: DndDropEvent, columnIndex: number): void {
        const index = event.index || 0;
        if (event.type === 'block') {
            const position = `${this.blockPosition}.${columnIndex}.${index}`;
            this.pageBuilderService.moveBlockToPosition(this.draggingBlock, position);
        } else if (event.type === 'blockType') {
            const position = `${this.blockPosition}.${columnIndex}.${index}`;
            this.pageBuilderService.addBlockOfType(this.draggingBlockType, position);
        }
    }

    onDragBlockStart(block: BlockModel, blockPosition: string): void {
        this.pageBuilderService.setDraggingBlock(block);
        this.pageBuilderService.setDraggingBlockType(block.type);
        this.pageBuilderService.setDraggingBlockPosition(blockPosition);
    }

    onDragBlockComplete(): void {
        this.pageBuilderService.setDraggingBlock(null);
        this.pageBuilderService.setDraggingBlockType(null);
        this.pageBuilderService.setDraggingBlockPosition(null);
    }

    preventPropagation(event: any): void {
        event.stopPropagation();
    }

    onAction(action: string, event?: any): void {
        super.onAction(action, event);
    }
}
